import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  Switch,
  FormControlLabel,
  TextField,
  Tabs,
  Tab,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Add as AddIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as ViewsIcon,
  ThumbUp as LikesIcon,
  Share as ShareIcon,
  Comment as CommentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';

const SocialMediaAutomation = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [createPostOpen, setCreatePostOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);

  const platforms = [
    {
      name: 'Facebook',
      icon: <FacebookIcon sx={{ color: '#1877F2' }} />,
      connected: true,
      followers: 8500,
      engagement: '4.2%',
      posts: 12,
    },
    {
      name: 'Instagram',
      icon: <InstagramIcon sx={{ color: '#E4405F' }} />,
      connected: true,
      followers: 15200,
      engagement: '6.8%',
      posts: 18,
    },
    {
      name: 'Twitter',
      icon: <TwitterIcon sx={{ color: '#1DA1F2' }} />,
      connected: false,
      followers: 0,
      engagement: '0%',
      posts: 0,
    },
    {
      name: 'LinkedIn',
      icon: <LinkedInIcon sx={{ color: '#0077B5' }} />,
      connected: true,
      followers: 3400,
      engagement: '3.1%',
      posts: 8,
    },
  ];

  const scheduledPosts = [
    {
      id: 1,
      title: 'Summer Furniture Collection Launch',
      content: 'Discover our new summer collection featuring modern outdoor furniture perfect for your patio! 🌞 #FurnitureDesign #Summer2024',
      platforms: ['Facebook', 'Instagram'],
      scheduledTime: '2024-01-22 10:00 AM',
      status: 'Scheduled',
      media: 'summer-collection.jpg',
      engagement: null,
    },
    {
      id: 2,
      title: 'Customer Testimonial - Modern Sofa',
      content: 'Thank you Sarah for sharing your beautiful living room setup with our Modern Sofa Collection! ✨ #CustomerLove #ModernFurniture',
      platforms: ['Instagram', 'Facebook'],
      scheduledTime: '2024-01-22 2:00 PM',
      status: 'Scheduled',
      media: 'customer-testimonial.jpg',
      engagement: null,
    },
    {
      id: 3,
      title: 'Behind the Scenes - Craftsmanship',
      content: 'Take a look at our master craftsmen creating beautiful handmade furniture. Quality you can see and feel! 🪚 #Craftsmanship #QualityFurniture',
      platforms: ['LinkedIn', 'Facebook'],
      scheduledTime: '2024-01-23 9:00 AM',
      status: 'Scheduled',
      media: 'craftsmanship-video.mp4',
      engagement: null,
    },
  ];

  const publishedPosts = [
    {
      id: 4,
      title: 'New Year, New Home Decor',
      content: 'Start 2024 with a fresh look! Our interior design team shares top trends for the new year. What\'s your favorite style? 🏠 #HomeDecor #2024Trends',
      platforms: ['Facebook', 'Instagram'],
      publishedTime: '2024-01-20 11:00 AM',
      status: 'Published',
      engagement: {
        views: 2400,
        likes: 156,
        shares: 23,
        comments: 12,
      },
    },
    {
      id: 5,
      title: 'Workspace Wednesday - Home Office Setup',
      content: 'Create the perfect work-from-home environment with our ergonomic office furniture collection. Productivity meets style! 💼 #WorkspaceWednesday #HomeOffice',
      platforms: ['LinkedIn', 'Instagram'],
      publishedTime: '2024-01-17 1:00 PM',
      status: 'Published',
      engagement: {
        views: 1800,
        likes: 89,
        shares: 15,
        comments: 7,
      },
    },
  ];

  const automationRules = [
    {
      id: 1,
      name: 'Product Showcase Automation',
      description: 'Automatically post new product arrivals across all platforms',
      trigger: 'New Product Added',
      platforms: ['Facebook', 'Instagram'],
      active: true,
      lastTriggered: '2024-01-20',
    },
    {
      id: 2,
      name: 'Customer Review Sharing',
      description: 'Share positive customer reviews and testimonials',
      trigger: '5-Star Review Received',
      platforms: ['Facebook', 'Instagram', 'LinkedIn'],
      active: true,
      lastTriggered: '2024-01-19',
    },
    {
      id: 3,
      name: 'Weekly Deal Promotion',
      description: 'Promote weekly deals every Monday morning',
      trigger: 'Weekly Schedule',
      platforms: ['Facebook', 'Instagram'],
      active: false,
      lastTriggered: null,
    },
  ];

  const analytics = {
    totalReach: 45600,
    totalEngagement: 2890,
    topPerformingPost: 'New Year, New Home Decor',
    engagementGrowth: '+15.2%',
    followerGrowth: '+8.5%',
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled':
        return 'warning';
      case 'Published':
        return 'success';
      case 'Draft':
        return 'info';
      default:
        return 'default';
    }
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Social Media Automation
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreatePostOpen(true)}
          sx={{ fontWeight: 'bold' }}
        >
          Create Post
        </Button>
      </Box>

      {/* Platform Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {platforms.map((platform, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {platform.icon}
                    <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
                      {platform.name}
                    </Typography>
                  </Box>
                  <Chip
                    label={platform.connected ? 'Connected' : 'Disconnected'}
                    color={platform.connected ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                {platform.connected && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Followers</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {platform.followers.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Engagement: {platform.engagement}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Posts this month: {platform.posts}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Analytics Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ViewsIcon color="primary" />
                <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
                  {analytics.totalReach.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">Total Reach</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LikesIcon color="info" />
                <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
                  {analytics.totalEngagement.toLocaleString()}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">Total Engagement</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon color="success" />
                <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
                  {analytics.engagementGrowth}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">Engagement Growth</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon color="warning" />
                <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
                  {analytics.followerGrowth}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">Follower Growth</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <CardContent>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab label="Scheduled Posts" />
            <Tab label="Published Posts" />
            <Tab label="Automation Rules" />
          </Tabs>

          {/* Scheduled Posts Tab */}
          <TabPanel value={currentTab} index={0}>
            {scheduledPosts.map((post) => (
              <Paper key={post.id} variant="outlined" sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {post.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {post.content}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      {post.platforms.map((platform) => (
                        <Chip key={platform} label={platform} size="small" />
                      ))}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      <ScheduleIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                      Scheduled for: {post.scheduledTime}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label={post.status}
                      color={getStatusColor(post.status)}
                      size="small"
                    />
                    <IconButton size="small">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            ))}
          </TabPanel>

          {/* Published Posts Tab */}
          <TabPanel value={currentTab} index={1}>
            {publishedPosts.map((post) => (
              <Paper key={post.id} variant="outlined" sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {post.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {post.content}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      {post.platforms.map((platform) => (
                        <Chip key={platform} label={platform} size="small" />
                      ))}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Published: {post.publishedTime}
                    </Typography>
                    {post.engagement && (
                      <Grid container spacing={2}>
                        <Grid item xs={3}>
                          <Typography variant="body2" color="text.secondary">Views</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {post.engagement.views.toLocaleString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body2" color="text.secondary">Likes</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {post.engagement.likes}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body2" color="text.secondary">Shares</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {post.engagement.shares}
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body2" color="text.secondary">Comments</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {post.engagement.comments}
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                  <Chip
                    label={post.status}
                    color={getStatusColor(post.status)}
                    size="small"
                  />
                </Box>
              </Paper>
            ))}
          </TabPanel>

          {/* Automation Rules Tab */}
          <TabPanel value={currentTab} index={2}>
            {automationRules.map((rule) => (
              <Paper key={rule.id} variant="outlined" sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {rule.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {rule.description}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Trigger:</strong> {rule.trigger}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      {rule.platforms.map((platform) => (
                        <Chip key={platform} label={platform} size="small" />
                      ))}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Last triggered: {rule.lastTriggered || 'Never'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                      control={<Switch checked={rule.active} />}
                      label={rule.active ? 'Active' : 'Inactive'}
                    />
                    <IconButton size="small">
                      <EditIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            ))}
          </TabPanel>
        </CardContent>
      </Card>

      {/* Create Post Dialog */}
      <Dialog open={createPostOpen} onClose={() => setCreatePostOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Social Media Post</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Post Title"
            margin="normal"
            placeholder="Enter a descriptive title for your post"
          />
          <TextField
            fullWidth
            label="Content"
            multiline
            rows={4}
            margin="normal"
            placeholder="Write your post content here..."
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Select Platforms</Typography>
            {platforms.filter(p => p.connected).map((platform) => (
              <FormControlLabel
                key={platform.name}
                control={<Switch />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {platform.icon}
                    <Typography sx={{ ml: 1 }}>{platform.name}</Typography>
                  </Box>
                }
              />
            ))}
          </Box>
          <TextField
            fullWidth
            label="Schedule Date & Time"
            type="datetime-local"
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatePostOpen(false)}>Cancel</Button>
          <Button variant="outlined">Save as Draft</Button>
          <Button variant="contained">Schedule Post</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SocialMediaAutomation;
